/**
 * @copyright Microsoft Corporation. All rights reserved.
 *
 * @file Analytics Stats component
 */

import * as React from 'react';
import { Link } from 'office-ui-fabric-react/lib/Link';
import { IPersonaProps } from 'office-ui-fabric-react/lib/Persona';
import { ActivityItem } from 'office-ui-fabric-react/lib/components/ActivityItem/index';
import { IActivity, IMAGE_LINK, IIncompleteData } from '../../../models/ItemAnalytics';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { mergeStyles, IStyle} from 'office-ui-fabric-react/lib/Styling';
import strings from '../FileHoverCard.resx';
import { TooltipHost } from 'office-ui-fabric-react/lib/Tooltip';
import * as AnalyticsStatsStyle from './AnalyticsStats.scss';

const DATE_NULL_VALUE = new Date('0001-01-01T00:00:00-08:00');

export interface IAnalyticsStatsProps {
  viewCount?: number;
  viewerCount?: number;
  activities?: IActivity[];
  inCompleteData?: IIncompleteData;
  withFacePile?: boolean;
  isCompact?: boolean;
  onClick?: (ev: React.MouseEvent<HTMLElement>) => void;
  isNotSupported?: boolean;
  disableViewerCount?: boolean;
  ODBOptOutViewerList?: boolean;
}

export interface IAnalyticsStatsStrings {
  view: string;
  views: string;
  viewer: string;
  viewers: string;
  apiError?: string;
  suggestion?: string;
  unsupportMessage?: string;
}

export const AnalyticsStats: React.StatelessComponent<IAnalyticsStatsProps> = (props: IAnalyticsStatsProps) => {
  const {
    viewCount,
    viewerCount,
    activities,
    inCompleteData,
    onClick,
    withFacePile,
    isCompact,
    disableViewerCount = true,
    ODBOptOutViewerList = false
  } = props;

  if (!(viewCount && viewerCount)) {
    return null;
  }

  if (!activities || !viewCount || !viewerCount) {
    return (
      <ActivityItem
        className={ `${AnalyticsStatsStyle.msAnalyticsStatsStats}` }
        isCompact={ true }
      />
    );
  }
  // const { viewCount, viewerCount, activities } = itemAnalytics;
  const analyticsInComplete = inCompleteData && inCompleteData.missingDataBeforeDateTime &&
    (new Date(inCompleteData.missingDataBeforeDateTime).getTime() !== DATE_NULL_VALUE.getTime());
  const wasThrottled = inCompleteData && inCompleteData.wasThrottled;
  const InfoMessage = analyticsInComplete ? strings.inCompleteMessage : (wasThrottled ? strings.throttledStateMessage : '');
  const teachingMessage = disableViewerCount ? (ODBOptOutViewerList ? strings.odbTeachingMessage : '') : strings.spoTeachingMessage;
  const notifyMessage = InfoMessage || teachingMessage;
  let viewCountString = viewCount > 1 ? strings.views : strings.view;
  let viewerCountString = viewerCount > 1 ? strings.viewers : strings.viewer;

  const statsElem =
    isCompact ?
      ((withFacePile && activities && activities.length !== 0) ?
        (
          <Link
            className={ `${AnalyticsStatsStyle.msAnalyticsStatsStatsStatsLink}` }
            onClick={ onClick }
          >
            <ActivityItem
              activityPersonas={
                (activities.length !== 0)
                  ? activities.slice(0, 3).map((activity: IActivity) => {
                    return {
                      imageUrl: activity && activity.actor && activity.actor.user && activity.actor.user.image
                    };
                  }).reverse()
                  : getBlankFacePile(viewerCount)
              }
              activityDescriptionText={ `${viewCount} ${viewCountString}` }
              isCompact={ true }
            />
          </Link>
        ):
        (
          <div className={ `${AnalyticsStatsStyle.msAnalyticsStatsViewsNoLink}` }>
            { `${viewCount} ${viewCountString}` }
            <span className={ `${AnalyticsStatsStyle.msAnalyticsStatsViewsDividerNoDot}` }/>
            { `${viewerCount} ${viewerCountString}` }
            { notifyMessage &&
              <TooltipHost content={ notifyMessage }>
                <Icon
                  className={ `${AnalyticsStatsStyle.msAnalyticsStatsTeachingMessage}` }
                  iconName={ 'Info' }
                  aria-describedby={ notifyMessage }
                />
              </TooltipHost> }
          </div>
        )
      )
      :
      (
      <div className={ `${AnalyticsStatsStyle.msAnalyticsStatsViews}` }>
        { `${viewerCount} ${viewerCountString}` }
        <span className={ `${AnalyticsStatsStyle.msAnalyticsStatsViewsDivider}` }/>
        { `${viewCount} ${viewCountString}` }
        { InfoMessage && (
          <TooltipHost content={ InfoMessage }>
            <Icon
              className={ `${AnalyticsStatsStyle.msAnalyticsStatsInfo}` }
              iconName={ 'Info' }
              aria-describedby={ InfoMessage }
            />
          </TooltipHost>) }
      </div >
      );

  return statsElem;
};

AnalyticsStats.defaultProps = {
  withFacePile: false
};

export function getMessageSection(iconName: string, content: string, iconStyle?: IStyle): JSX.Element {
  const baseIconStyle: IStyle = {
    paddingRight: '8px',
    position: 'relative'
  };

  return (
    <div className={ `${AnalyticsStatsStyle.msAnalyticsStatsErrorMessage}` }>
      <Icon
        iconName={ iconName }
        className={ `${AnalyticsStatsStyle.msFileHoverCardErrorIcon}` }
        styles={ {
          root: mergeStyles([
            baseIconStyle,
            iconStyle
          ])
        } }
      />
      { content }
    </div>
  );
}

function getBlankFacePile(viewer: number): IPersonaProps[] | undefined {
  const count = Math.min(3, viewer);
  if (count === 0) {
    return undefined;
  }

  let facepiles: IPersonaProps[] = [];
  for (let i = 0; i < count; i++) {
    facepiles.push({
      imageUrl: IMAGE_LINK
    });
  }
  return facepiles;
}